/**
 * Portal Service
 * API calls for business users portal
 */

import api from './api';
import type {
  PortalApiResponse,
  PortalDashboardStats,
  PortalUserInfo,
  PortalProduct,
  PortalService,
  PortalPayment,
  PortalEvent,
  PortalCampaign,
  PortalOrder,
  PortalReportSummary,
  BusinessRegistrationData,
  BusinessRegistration,
} from '../types/portal.types';

// ============================================
// Dashboard & Profile
// ============================================

export const getDashboard = async (): Promise<PortalDashboardStats> => {
  const response = await api.get<PortalApiResponse<PortalDashboardStats>>('/portal/dashboard');
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data!;
};

export const getStats = async (periodo?: number): Promise<any> => {
  const response = await api.get('/portal/stats', { params: { periodo } });
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const getProfile = async (): Promise<any> => {
  const response = await api.get('/portal/profile');
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const updateProfile = async (data: any): Promise<any> => {
  const response = await api.put('/portal/profile', data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const getMe = async (): Promise<PortalUserInfo> => {
  const response = await api.get<PortalApiResponse<PortalUserInfo>>('/portal/me');
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data!;
};

export const getPermissions = async (): Promise<any> => {
  const response = await api.get('/portal/permissions');
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

// ============================================
// Products (PFLOJ)
// ============================================

export const getProducts = async (params?: any): Promise<PortalApiResponse<PortalProduct[]>> => {
  const response = await api.get('/portal/products', { params });
  return response.data;
};

export const getProduct = async (id: string): Promise<PortalProduct> => {
  const response = await api.get<PortalApiResponse<PortalProduct>>(`/portal/products/${id}`);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data!;
};

export const createProduct = async (data: Partial<PortalProduct>): Promise<PortalProduct> => {
  const response = await api.post<PortalApiResponse<PortalProduct>>('/portal/products', data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data!;
};

export const updateProduct = async (id: string, data: Partial<PortalProduct>): Promise<PortalProduct> => {
  const response = await api.put<PortalApiResponse<PortalProduct>>(`/portal/products/${id}`, data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data!;
};

export const deleteProduct = async (id: string): Promise<void> => {
  const response = await api.delete(`/portal/products/${id}`);
  if (!response.data.success) throw new Error(response.data.error);
};

export const getProductDigital = async (productId: string): Promise<any> => {
  const response = await api.get(`/portal/digital/products/${productId}`);
  return response.data.data;
};

export const uploadDigitalFile = async (productId: string, file: File): Promise<any> => {
  const fileBase64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const response = await api.post(`/portal/digital/products/${productId}/upload`, {
    fileBase64,
    mimeType: file.type,
    fileName: file.name,
  });
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const attachDigitalFile = async (productId: string, data: {
  ficheiroUrl: string; storagePath?: string; nomeFicheiro?: string;
  formato?: string; tamanhoMb?: number;
}): Promise<void> => {
  const response = await api.post(`/portal/digital/products/${productId}/attach`, data);
  if (!response.data.success) throw new Error(response.data.error);
};

export const detachDigitalFile = async (productId: string): Promise<void> => {
  const response = await api.delete(`/portal/digital/products/${productId}/detach`);
  if (!response.data.success) throw new Error(response.data.error);
};

export const getInventory = async (alertas?: boolean): Promise<any> => {
  const response = await api.get('/portal/products/inventory/list', { params: { alertas } });
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const updateStock = async (id: string, stock: number, operacao?: 'set' | 'add' | 'subtract'): Promise<any> => {
  const response = await api.put(`/portal/products/inventory/${id}`, { stock, operacao });
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const getOrders = async (params?: any): Promise<PortalApiResponse<PortalOrder[]>> => {
  const response = await api.get('/portal/products/orders/list', { params });
  return response.data;
};

export const updateOrderStatus = async (id: string, estado: string): Promise<void> => {
  const response = await api.put(`/portal/products/orders/${id}/status`, { estado });
  if (!response.data.success) throw new Error(response.data.error);
};

export const getProductCategories = async (): Promise<any[]> => {
  const response = await api.get('/portal/products/categories/list');
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const createCategory = async (data: { nome: string; imagem?: string; mimeType?: string; icone?: string }): Promise<any> => {
  const response = await api.post('/portal/products/categories', data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const updateCategory = async (id: string, data: { nome?: string; estado?: string; imagem?: string; mimeType?: string; icone?: string }): Promise<any> => {
  const response = await api.put(`/portal/products/categories/${id}`, data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data;
};

// Variation Types
export const getVariationTypes = async (): Promise<any[]> => {
  const response = await api.get('/portal/products/variations/types');
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const createVariationType = async (data: { nome: string; opcoes?: any[] }): Promise<any> => {
  const response = await api.post('/portal/products/variations/types', data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const deleteVariationType = async (id: string): Promise<void> => {
  const response = await api.delete(`/portal/products/variations/types/${id}`);
  if (!response.data.success) throw new Error(response.data.error);
};

export const addVariationOption = async (data: { tipoId: string; valor: string; codigoCor?: string }): Promise<any> => {
  const response = await api.post('/portal/products/variations/options', data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const deleteVariationOption = async (id: string): Promise<void> => {
  const response = await api.delete(`/portal/products/variations/options/${id}`);
  if (!response.data.success) throw new Error(response.data.error);
};

// Product Variations
export const getProductVariations = async (produtoId: string): Promise<any[]> => {
  const response = await api.get(`/portal/products/${produtoId}/variations`);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const createProductVariation = async (produtoId: string, data: any): Promise<any> => {
  const response = await api.post(`/portal/products/${produtoId}/variations`, data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const deleteProductVariation = async (variacaoId: string): Promise<void> => {
  const response = await api.delete(`/portal/products/variations/${variacaoId}`);
  if (!response.data.success) throw new Error(response.data.error);
};

// Announcements
export const getAnnouncements = async (): Promise<any[]> => {
  const response = await api.get('/portal/products/announcements/list');
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const createAnnouncement = async (data: { texto: string; corFundo?: string; corTexto?: string }): Promise<any> => {
  const response = await api.post('/portal/products/announcements', data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const updateAnnouncement = async (id: string, data: any): Promise<any> => {
  const response = await api.put(`/portal/products/announcements/${id}`, data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data;
};

export const deleteAnnouncement = async (id: string): Promise<void> => {
  const response = await api.delete(`/portal/products/announcements/${id}`);
  if (!response.data.success) throw new Error(response.data.error);
};

export const deleteCategory = async (id: string): Promise<void> => {
  const response = await api.delete(`/portal/products/categories/${id}`);
  if (!response.data.success) throw new Error(response.data.error);
};

// ============================================
// Services (PFEMP, PFGOV, PFPRO)
// ============================================

export const getServices = async (params?: any): Promise<PortalApiResponse<PortalService[]>> => {
  const response = await api.get('/portal/services', { params });
  return response.data;
};

export const getService = async (id: string): Promise<PortalService> => {
  const response = await api.get<PortalApiResponse<PortalService>>(`/portal/services/${id}`);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data!;
};

export const createService = async (data: any): Promise<PortalService> => {
  const response = await api.post<PortalApiResponse<PortalService>>('/portal/services', data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data!;
};

export const updateService = async (id: string, data: any): Promise<PortalService> => {
  const response = await api.put<PortalApiResponse<PortalService>>(`/portal/services/${id}`, data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data!;
};

export const deleteService = async (id: string): Promise<void> => {
  const response = await api.delete(`/portal/services/${id}`);
  if (!response.data.success) throw new Error(response.data.error);
};

export const toggleService = async (id: string): Promise<void> => {
  const response = await api.put(`/portal/services/${id}/toggle`);
  if (!response.data.success) throw new Error(response.data.error);
};

export const getServiceCategories = async (): Promise<any[]> => {
  const response = await api.get('/portal/services/categories/list');
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const getServiceTypes = async (): Promise<any[]> => {
  const response = await api.get('/portal/services/types/list');
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

// ============================================
// Payments
// ============================================

export const getPayments = async (params?: any): Promise<PortalApiResponse<PortalPayment[]>> => {
  const response = await api.get('/portal/payments', { params });
  return response.data;
};

export const getPayment = async (id: string): Promise<PortalPayment> => {
  const response = await api.get<PortalApiResponse<PortalPayment>>(`/portal/payments/${id}`);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data!;
};

export const requestRefund = async (id: string, motivo: string, valorParcial?: number): Promise<void> => {
  const response = await api.post(`/portal/payments/${id}/refund`, { motivo, valorParcial });
  if (!response.data.success) throw new Error(response.data.error);
};

export const getPaymentsSummary = async (periodo?: number): Promise<any> => {
  const response = await api.get('/portal/payments/stats/summary', { params: { periodo } });
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const getPaymentsChart = async (periodo?: number, agrupamento?: string): Promise<any> => {
  const response = await api.get('/portal/payments/stats/chart', { params: { periodo, agrupamento } });
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

// ============================================
// Reports
// ============================================

export const getReportSummary = async (params?: any): Promise<PortalReportSummary> => {
  const response = await api.get<PortalApiResponse<PortalReportSummary>>('/portal/reports/summary', { params });
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data!;
};

export const getReportTransactions = async (params?: any): Promise<PortalApiResponse<any[]>> => {
  const response = await api.get('/portal/reports/transactions', { params });
  return response.data;
};

export const getReportDaily = async (dias?: number): Promise<any[]> => {
  const response = await api.get('/portal/reports/daily', { params: { dias } });
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const getReportMonthly = async (meses?: number): Promise<any[]> => {
  const response = await api.get('/portal/reports/monthly', { params: { meses } });
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const getReportByServices = async (params?: any): Promise<any[]> => {
  const response = await api.get('/portal/reports/services', { params });
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const exportReport = async (tipo: string, formato: string, params?: any): Promise<any> => {
  const response = await api.get('/portal/reports/export', { params: { tipo, formato, ...params } });
  return response.data;
};

// ============================================
// Events (PFEVT)
// ============================================

export const getEvents = async (params?: any): Promise<PortalApiResponse<PortalEvent[]>> => {
  const response = await api.get('/portal/events', { params });
  return response.data;
};

export const getEvent = async (id: string): Promise<PortalEvent> => {
  const response = await api.get<PortalApiResponse<PortalEvent>>(`/portal/events/${id}`);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data!;
};

export const createEvent = async (data: any): Promise<PortalEvent> => {
  const response = await api.post<PortalApiResponse<PortalEvent>>('/portal/events', data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data!;
};

export const updateEvent = async (id: string, data: any): Promise<PortalEvent> => {
  const response = await api.put<PortalApiResponse<PortalEvent>>(`/portal/events/${id}`, data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data!;
};

export const deleteEvent = async (id: string): Promise<void> => {
  const response = await api.delete(`/portal/events/${id}`);
  if (!response.data.success) throw new Error(response.data.error);
};

export const getEventTickets = async (eventId: string, params?: any): Promise<any> => {
  const response = await api.get(`/portal/events/${eventId}/tickets`, { params });
  return response.data;
};

export const validateTicket = async (eventId: string, codigo?: string, bilheteId?: string): Promise<any> => {
  const response = await api.post(`/portal/events/${eventId}/tickets/validate`, { codigo, bilheteId });
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const getEventCheckins = async (eventId: string): Promise<any> => {
  const response = await api.get(`/portal/events/${eventId}/checkins`);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const createTicketType = async (eventId: string, data: any): Promise<any> => {
  const response = await api.post(`/portal/events/${eventId}/ticket-types`, data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

// ============================================
// Donations (PFONG)
// ============================================

export const getCampaigns = async (params?: any): Promise<PortalApiResponse<PortalCampaign[]>> => {
  const response = await api.get('/portal/donations', { params });
  return response.data;
};

export const getCampaign = async (id: string): Promise<PortalCampaign> => {
  const response = await api.get<PortalApiResponse<PortalCampaign>>(`/portal/donations/${id}`);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data!;
};

export const createCampaign = async (data: any): Promise<PortalCampaign> => {
  const response = await api.post<PortalApiResponse<PortalCampaign>>('/portal/donations', data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data!;
};

export const updateCampaign = async (id: string, data: any): Promise<PortalCampaign> => {
  const response = await api.put<PortalApiResponse<PortalCampaign>>(`/portal/donations/${id}`, data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data!;
};

export const deleteCampaign = async (id: string): Promise<void> => {
  const response = await api.delete(`/portal/donations/${id}`);
  if (!response.data.success) throw new Error(response.data.error);
};

export const getCampaignDonors = async (id: string, params?: any): Promise<any> => {
  const response = await api.get(`/portal/donations/${id}/donors`, { params });
  return response.data;
};

export const postCampaignUpdate = async (id: string, data: any): Promise<any> => {
  const response = await api.post(`/portal/donations/${id}/updates`, data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const createReward = async (campaignId: string, data: any): Promise<any> => {
  const response = await api.post(`/portal/donations/${campaignId}/rewards`, data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const getAllDonors = async (params?: any): Promise<any> => {
  const response = await api.get('/portal/donations/all/donors', { params });
  return response.data;
};

// ============================================
// Business Registration
// ============================================

export const getBusinessTypes = async (): Promise<any[]> => {
  const response = await api.get('/business/types');
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const registerBusiness = async (data: BusinessRegistrationData): Promise<any> => {
  const response = await api.post('/business/register', data);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const checkRegistrationStatus = async (id: string): Promise<BusinessRegistration> => {
  const response = await api.get<PortalApiResponse<BusinessRegistration>>(`/business/register/${id}/status`);
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data!;
};

// ============================================
// API MANAGEMENT
// ============================================

export const getApiKeys = async (): Promise<any[]> => {
  const response = await api.get('/portal/api/keys');
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data || [];
};

export const getApiUsage = async (): Promise<any> => {
  const response = await api.get('/portal/api/usage');
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export const getApiBilling = async (): Promise<any> => {
  const response = await api.get('/portal/api/billing');
  if (!response.data.success) throw new Error(response.data.error);
  return response.data.data;
};

export default {
  // Dashboard
  getDashboard,
  getStats,
  getProfile,
  updateProfile,
  getMe,
  getPermissions,
  // Products
  getProducts,
  getProduct,
  createProduct,
  updateProduct,
  deleteProduct,
  getInventory,
  updateStock,
  getOrders,
  updateOrderStatus,
  getProductCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  // Variations
  getVariationTypes,
  createVariationType,
  deleteVariationType,
  addVariationOption,
  deleteVariationOption,
  getProductVariations,
  createProductVariation,
  deleteProductVariation,
  // Announcements
  getAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  // Services
  getServices,
  getService,
  createService,
  updateService,
  deleteService,
  toggleService,
  getServiceCategories,
  getServiceTypes,
  // Payments
  getPayments,
  getPayment,
  requestRefund,
  getPaymentsSummary,
  getPaymentsChart,
  // Reports
  getReportSummary,
  getReportTransactions,
  getReportDaily,
  getReportMonthly,
  getReportByServices,
  exportReport,
  // Events
  getEvents,
  getEvent,
  createEvent,
  updateEvent,
  deleteEvent,
  getEventTickets,
  validateTicket,
  getEventCheckins,
  createTicketType,
  // Donations
  getCampaigns,
  getCampaign,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  getCampaignDonors,
  postCampaignUpdate,
  createReward,
  getAllDonors,
  // Business Registration
  getBusinessTypes,
  registerBusiness,
  checkRegistrationStatus,
  // API Management
  getApiKeys,
  getApiUsage,
  getApiBilling,
};
